import { useEffect, useState } from 'react'
import { ExtractionRunDetailView } from '@/components/documents/ExtractionRunDetailView'
import { useAuth } from '@/contexts/AuthContext'
import { api } from '@/services/api'
import type { ExtractionRunDetail as ExtractionRunDetailType } from '@/services/api/cases'

interface ExtractionRunDetailProps {
  caseId: string
  runId: string
  onClose: () => void
}

/**
 * Connected extraction-run detail panel. Loads the run detail for `runId`,
 * polls every 5s while the run is still running, and feeds the pure
 * {@link ExtractionRunDetailView}. Routing-reasoning panels are gated on the
 * current user's superuser flag.
 */
export function ExtractionRunDetail({ caseId, runId, onClose }: ExtractionRunDetailProps) {
  const { user } = useAuth()
  const showRouting = !!user?.is_superuser
  const [data, setData] = useState<ExtractionRunDetailType | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    api.cases
      .getExtractionRunDetail(caseId, runId)
      .then(result => {
        if (!cancelled) {
          setData(result)
          setLoading(false)
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err?.message || 'Failed to load')
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [caseId, runId])

  useEffect(() => {
    if (!data || data.status !== 'running') return
    const id = setInterval(async () => {
      try {
        const result = await api.cases.getExtractionRunDetail(caseId, runId)
        setData(result)
      } catch {
        // ignore — keep the last good snapshot until the next poll succeeds.
      }
    }, 5000)
    return () => clearInterval(id)
  }, [caseId, runId, data?.status, data])

  return (
    <ExtractionRunDetailView
      data={data}
      loading={loading}
      error={error}
      showRouting={showRouting}
      onClose={onClose}
    />
  )
}
